/**
 * Notifications: the catalogue, template versions, quiet hours and delivery forensics.
 *
 * **The catalogue is read-only, and the screen says so out loud.** It is code — a new event is a
 * backend change. An "add event" button that could not work would be worse than the absence.
 *
 * **Coverage gaps are named, not blank.** A missing template is a message that will never arrive;
 * an empty cell reads as "nothing to do", so every gap is listed with its event, channel and
 * locale.
 *
 * **The channel gates are shown before they bite.** An SMS without a registered DLT id and a
 * WhatsApp template the BSP has not approved cannot activate — the row says which, so the
 * operator learns it here rather than from a suppressed booking confirmation hours later.
 *
 * **Forensics shows the refusals as much as the sends.** "It was not sent, and here is which rule
 * stopped it" is the answer support needs; a screen of successes makes every suppression look
 * like a bug.
 *
 * **The search box filters two complete lists, not one page of many.** The catalogue and the
 * template list are unpaged, so narrowing them locally is not the "3 results" lie a client-side
 * filter over a paged table would be. Delivery forensics IS paged, and is filtered server-side.
 *
 * The campaign builder lives in its own file (`CampaignBuilder.tsx`, CSR-033): two-figure
 * audience, test-send before send, and a confirm that makes the operator type the reach.
 */
import { useState } from 'react';
import { Chip, StatusChip } from '../components/primitives';
import { DataTable } from '../components/data';
import { Toggle } from '../components/forms';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, FilterBar, ScopeAction, Section, StatTile } from '../components/kit';
import { useConsole } from '../app/console';
import { CampaignsTab } from './CampaignBuilder';
import { DeliverabilityPanel } from './DeliverabilityPanel';
import { GatewayRoutes } from './GatewayRoutes';
import { SuppressionList } from './SuppressionList';
import { TemplateEditor, TemplatePreviewPanel } from './TemplateEditor';
import { errorMessage } from '../lib/api/client';
import {
  useActivateTemplate,
  useDeactivateTemplate,
  useNotificationCatalogue,
  useNotificationMessages,
  useNotificationTemplates,
  useQuietHours,
  useSetQuietHours,
  type CatalogueEventRow,
  type TemplateRow,
} from '../lib/api/hooks/notifications';
import { useUrlSearch, useUrlTab } from '../lib/filterCodecs';
import { formatDateTime } from '../lib/datetime';

const TABS = [
  { key: 'templates', label: 'Templates' },
  { key: 'quiet', label: 'Quiet hours' },
  { key: 'messages', label: 'Delivery forensics' },
  { key: 'events', label: 'Event catalogue' },
  { key: 'campaigns', label: 'Campaigns' },
];

/** The tab rides the URL (doc 05-admin/04 §3.3), so a pasted link opens the same tab. */
const TAB_KEYS = TABS.map((t) => t.key);

/** What the gate will do to a message of this class — the behaviour contract, in one phrase. */
export function classBehaviour(row: CatalogueEventRow): string {
  if (row.message_class === 'otp') return 'Never gated, never held';
  if (row.is_consent_gated) return 'Opt-in, capped, always unsubscribable';
  if (row.is_held_by_quiet_hours) return 'On by default, deferred in quiet hours';
  return 'Never gated, never held';
}

export function statusTone(status: string): 'neutral' | 'warn' | 'success' | 'error' {
  if (status === 'sent' || status === 'delivered') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'suppressed') return 'warn';
  return 'neutral';
}

/**
 * Why this template cannot go live yet, or an empty string.
 *
 * Shown on the row rather than only on the failed activation, so the reason arrives before
 * somebody presses the button and not as a rejection afterwards.
 */
export function activationBlocker(row: TemplateRow): string {
  if (row.channel === 'sms' && !row.dlt_template_id) return 'Needs a registered DLT id';
  // MSG91 sends by its own template id, not the DLT id; the API refuses activation without it.
  if (row.channel === 'sms' && !row.provider_template_id) return 'Needs its MSG91 template id';
  if (row.channel === 'whatsapp' && row.whatsapp_approval_status !== 'approved') {
    return 'Needs BSP approval';
  }
  if (row.channel === 'whatsapp' && !row.whatsapp_template_name) return 'Needs its approved name';
  return '';
}

export function Campaigns() {
  const { scope } = useConsole();
  const [tab, setTab] = useUrlTab('campaigns', TAB_KEYS, 'templates');
  const [q, setQ] = useUrlSearch('campaigns');
  const [editing, setEditing] = useState<TemplateRow | null>(null);
  const [previewing, setPreviewing] = useState<TemplateRow | null>(null);
  const [activating, setActivating] = useState<TemplateRow | null>(null);

  const catalogue = useNotificationCatalogue();
  const templates = useNotificationTemplates();
  const quietHours = useQuietHours();
  const messages = useNotificationMessages({ page: 1, page_size: 50 });
  const activate = useActivateTemplate();
  const deactivate = useDeactivateTemplate();
  const setWindow = useSetQuietHours();

  const gaps = catalogue.data?.gaps ?? [];
  const counts = messages.data?.counts_by_status;

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
          gap: 'var(--sp-md)',
        }}
      >
        <StatTile
          label="Declared events"
          value={catalogue.data?.items.length ?? '—'}
          note="the catalogue is code"
        />
        <StatTile
          label="Coverage gaps"
          value={catalogue.isSuccess ? gaps.length : '—'}
          note="events with no live template"
          tone={gaps.length > 0 ? 'error' : 'default'}
        />
        <StatTile
          label="Suppressed"
          value={counts?.suppressed ?? '—'}
          note="blocked by a rule, with the reason"
        />
        <StatTile label="Failed" value={counts?.failed ?? '—'} note="the provider said no" />
      </div>

      <FilterBar q={q} onQ={setQ} placeholder="Event — press /" tabs={TABS} tab={tab} onTab={setTab} />

      {tab === 'templates' && (
        <>
          {templates.isPending && <LoadingState message="Reading templates…" />}
          {templates.isError && (
            <ErrorState title="Could not read templates" cause={errorMessage(templates.error)} />
          )}
          {templates.isSuccess && templates.data.items.length === 0 && (
            <EmptyState
              icon="megaphone"
              title="No templates yet"
              body="Every declared event needs copy in English and Hindi before it can send anything. The catalogue tab lists what is missing."
              inline
            />
          )}
          {templates.isSuccess && templates.data.items.length > 0 && (
            <Section
              title="Template versions"
              note="Old versions stay — sent messages pin the one they used"
              pad={false}
            >
              <DataTable
                density="compact"
                columns={[
                  { key: 'event', label: 'Event' },
                  { key: 'channel', label: 'Channel' },
                  { key: 'locale', label: 'Locale' },
                  { key: 'version', label: 'Version', align: 'right', mono: true },
                  { key: 'state', label: 'State' },
                  { key: 'gate', label: 'Gate' },
                  { key: 'act', label: '' },
                  { key: 'edit', label: '' },
                ]}
                rows={templates.data.items
                  .filter((t) => t.event_type.toLowerCase().includes(q.toLowerCase()))
                  .map((t) => {
                    const blocker = activationBlocker(t);
                    return {
                      id: t.id,
                      event: t.event_type,
                      channel: <Chip variant="filter" label={t.channel} />,
                      locale: t.locale,
                      version: t.version,
                      state: (
                        <StatusChip
                          tone={t.is_active ? 'success' : 'neutral'}
                          label={t.is_active ? 'live' : 'draft'}
                        />
                      ),
                      gate: blocker ? <StatusChip tone="warn" label={blocker} /> : '—',
                      act: (
                        <ScopeAction
                          scope={scope}
                          need="growth"
                          size="sm"
                          variant={t.is_active ? 'secondary' : 'primary'}
                          disabled={!t.is_active && blocker !== ''}
                          onClick={() =>
                            t.is_active
                              ? deactivate.mutate(t.id)
                              : setActivating(t)
                          }
                        >
                          {t.is_active ? 'Stand down' : 'Activate'}
                        </ScopeAction>
                      ),
                      edit: (
                        <>
                          <ScopeAction
                            scope={scope}
                            need="growth"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setPreviewing(null);
                              setEditing(t);
                            }}
                          >
                            New version
                          </ScopeAction>
                          <ScopeAction
                            scope={scope}
                            need="growth"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditing(null);
                              setPreviewing(t);
                            }}
                          >
                            Preview
                          </ScopeAction>
                        </>
                      ),
                    };
                  })}
              />
            </Section>
          )}
          {editing !== null && (
            <TemplateEditor
              key={editing.id}
              base={editing}
              // Only reachable from a row of the loaded table.
              all={templates.data!.items}
              onClose={() => setEditing(null)}
            />
          )}
          {activating !== null && (
            // §3.1 item 6: activation is the moment copy reaches people, so it is typed, not clicked.
            <ConfirmAction
              open
              title={`Make v${activating.version} the live ${activating.channel} copy for ${activating.event_type} (${activating.locale})?`}
              phrase="ACTIVATE"
              consequences={[
                'Every message of this event, channel and locale sends this version from now on. The version it replaces stays, pinned on what it already sent.',
                'The content lint runs again on activation; a phrase that promises an outcome is refused, quoted.',
              ]}
              confirmLabel="Activate"
              onCancel={() => setActivating(null)}
              onConfirm={() => {
                activate.mutate(activating.id);
                setActivating(null);
              }}
            />
          )}
          {previewing !== null && (
            <TemplatePreviewPanel key={previewing.id} template={previewing} onClose={() => setPreviewing(null)} />
          )}
          {(activate.isError || deactivate.isError) && (
            <ErrorState
              title="That template could not change state"
              cause={errorMessage(activate.error ?? deactivate.error)}
              inline
            />
          )}
        </>
      )}

      {tab === 'quiet' && (
        <>
          {quietHours.isPending && <LoadingState message="Reading quiet hours…" />}
          {quietHours.isError && (
            <ErrorState title="Could not read quiet hours" cause={errorMessage(quietHours.error)} />
          )}
          {quietHours.isSuccess && (
            <Section
              title="Quiet hours"
              note="Reminders and marketing only — an OTP at 3am is somebody signing in at 3am"
              pad={false}
            >
              <DataTable
                density="compact"
                columns={[
                  { key: 'category', label: 'Category' },
                  { key: 'channel', label: 'Channel' },
                  { key: 'window', label: 'Window (IST)', mono: true },
                  { key: 'behaviour', label: 'Behaviour' },
                  { key: 'active', label: 'Active' },
                ]}
                rows={quietHours.data.items.map((w) => ({
                  id: w.id,
                  category: w.category,
                  channel: <Chip variant="filter" label={w.channel} />,
                  window: `${w.start_time}–${w.end_time}`,
                  behaviour: (
                    <StatusChip
                      tone={w.behaviour === 'suppress' ? 'warn' : 'neutral'}
                      label={w.behaviour === 'suppress' ? 'dropped' : 'deferred to the edge'}
                    />
                  ),
                  active: (
                    <Toggle
                      on={w.is_active}
                      label={`${w.category} on ${w.channel}`}
                      onChange={(next) =>
                        setWindow.mutate({
                          policyId: w.id,
                          start_time: w.start_time,
                          end_time: w.end_time,
                          behaviour: w.behaviour,
                          is_active: next,
                        })
                      }
                    />
                  ),
                }))}
              />
            </Section>
          )}
          {setWindow.isError && (
            <ErrorState
              title="That window could not be changed"
              cause={errorMessage(setWindow.error)}
              inline
            />
          )}
          <SuppressionList />
          <GatewayRoutes />
        </>
      )}

      {tab === 'messages' && (
        <>
          <DeliverabilityPanel />
          {messages.isPending && <LoadingState message="Reading delivery history…" />}
          {messages.isError && (
            <ErrorState title="Could not read delivery history" cause={errorMessage(messages.error)} />
          )}
          {messages.isSuccess && messages.data.items.length === 0 && (
            <EmptyState
              icon="bell-off"
              title="Nothing has been sent yet"
              body="Every attempt lands here — including the ones a rule refused, with the reason."
              inline
            />
          )}
          {messages.isSuccess && messages.data.items.length > 0 && (
            <Section
              title="Delivery forensics"
              note={`${messages.data.total} attempts · destinations are hashed, never stored`}
              pad={false}
            >
              <DataTable
                density="compact"
                columns={[
                  { key: 'channel', label: 'Channel' },
                  { key: 'status', label: 'Status' },
                  { key: 'reason', label: 'Why not' },
                  { key: 'provider', label: 'Provider' },
                  { key: 'version', label: 'Template', align: 'right', mono: true },
                  { key: 'when', label: 'When', mono: true },
                ]}
                rows={messages.data.items.map((m) => ({
                  id: m.id,
                  channel: <Chip variant="filter" label={m.channel} />,
                  status: <StatusChip tone={statusTone(m.status)} label={m.status} />,
                  reason: m.suppression_reason ?? m.failed_reason ?? '—',
                  provider: m.provider ?? '—',
                  version: `v${m.template_version}`,
                  when: formatDateTime(m.created_at),
                }))}
              />
            </Section>
          )}
        </>
      )}

      {tab === 'events' && (
        <>
          {catalogue.isPending && <LoadingState message="Reading the catalogue…" />}
          {catalogue.isError && (
            <ErrorState title="Could not read the catalogue" cause={errorMessage(catalogue.error)} />
          )}
          {catalogue.isSuccess && (
            <>
              <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
                Read-only. The catalogue is code — adding an event is a backend change, not a
                console action.
              </p>

              {gaps.length > 0 && (
                <Section
                  title={`${gaps.length} coverage ${gaps.length === 1 ? 'gap' : 'gaps'}`}
                  note="A declared event with no live template is a message that will never arrive"
                >
                  <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: 'var(--fs-admin-cell)' }}>
                    {gaps.slice(0, 20).map((g) => (
                      <li key={`${g.event_type}-${g.channel}-${g.locale}`}>
                        {g.event_type} · {g.channel} · {g.locale}
                      </li>
                    ))}
                  </ul>
                  {gaps.length > 20 && (
                    <p style={{ margin: 0, fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
                      …and {gaps.length - 20} more.
                    </p>
                  )}
                </Section>
              )}

              <Section title="Event catalogue" pad={false}>
                <DataTable
                  density="compact"
                  columns={[
                    { key: 'event', label: 'Event' },
                    { key: 'channels', label: 'Channels (in order)' },
                    { key: 'class', label: 'Class' },
                    { key: 'behaviour', label: 'How the gate treats it' },
                    { key: 'purpose', label: 'What it is for' },
                  ]}
                  rows={catalogue.data.items
                    .filter((e) => e.event_type.toLowerCase().includes(q.toLowerCase()))
                    .map((e) => ({
                      id: e.event_type,
                      event: e.event_type,
                      channels: e.channels.join(' → '),
                      class: <Chip variant="filter" label={e.message_class} />,
                      behaviour: classBehaviour(e),
                      purpose: e.purpose,
                    }))}
                />
              </Section>
            </>
          )}
        </>
      )}

      {tab === 'campaigns' && <CampaignsTab scope={scope} />}
    </>
  );
}
